import {
  Box,
  BoxProps,
  CloseButton,
  Drawer,
  HStack,
  IconButton,
  Image,
  Portal,
  useBreakpointValue
} from '@chakra-ui/react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import {
  BaseButton,
  BaseNav,
  RouteAuthenticationRequirement,
  SupportButton,
  useAuth,
  useNavItemStore
} from '@invara-dev/react-base';
import { ApplicationRoutes } from '../../constants/appRoutes';
import logo from '../../assets/mopspot-logo.png';
import { AppRoles } from '../../constants/appRoles';
import { MopspotUser } from '../../globalModels/user/mopspotUser';
import { Sidebar } from '../sidebar/sidebar';
import { LuMenu } from 'react-icons/lu';
import { useLayoutStore } from '../../util/layoutStore';

type NarrowLayoutProps = {
  containerProps?: BoxProps;
};

export const Layout = (props: NarrowLayoutProps) => {
  const isDesktop = useBreakpointValue({ base: false, lg: true });
  const [open, setOpen] = useState(false);

  const nav = useNavigate();
  const routeNav = useNavItemStore((state) => {
    return { items: state.getDefault(), get: state.get };
  });

  const profileItems: BaseNav.NavItem = useMemo(() => {
    const subItems: BaseNav.NavItem[] = [
      routeNav.get(ApplicationRoutes.account.myProfile),
      routeNav.get(ApplicationRoutes.account.logout)
    ];
    return {
      text: 'Profile',
      subItems: subItems,
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      // @ts-ignore
      allowedRoles: []
    };
  }, []);

  const auth = useAuth<MopspotUser>();
  const isFullScreen = useLayoutStore((state) => state.isFullScreen);

  return (
    <Box as='section' width='100vw' height='100vh' overflowY='auto'>
      {!isFullScreen && (
        <BaseNav.Header
          navItems={isDesktop ? routeNav.items : []}
          profileItems={profileItems}
          beforeNavChildren={
            <>
              {auth.isAuthenticated && auth.isInRole([AppRoles.Manager, AppRoles.Admin]) && (
                <SupportButton useDisplayMedia={true} />
              )}
            </>
          }
          avatarName={`${auth.user?.FirstName} ${auth.user?.LastName}`}
          barColor={'secondary.500'}
          logo={
            isDesktop ? (
              <HStack hideBelow={'md'}>
                <Image paddingTop={'4px'} height={['35px']} src={logo} alt='logo' />
              </HStack>
            ) : (
              <Box>
                <IconButton
                  hideFrom={'lg'}
                  aria-label='Open Menu'
                  onClick={() => {
                    setOpen(true);
                  }}>
                  <LuMenu />
                </IconButton>
              </Box>
            )
          }>
          {auth.isAuthenticated && auth.isInRole([AppRoles.TeamMember]) && (
            <Box>
              <BaseButton size={'xl'} onClick={() => nav(ApplicationRoutes.cleaning.startCleaning)}>
                Start Cleaning
              </BaseButton>
            </Box>
          )}
        </BaseNav.Header>
      )}

      {!isFullScreen && (
        <Drawer.Root open={open} placement={'start'} onOpenChange={(e: { open: boolean }) => setOpen(e.open)}>
          <Portal>
            <Drawer.Backdrop />
            <Drawer.Positioner>
              <Drawer.Content>
                <Drawer.Body>
                  <Sidebar showAdminNav={true} />
                </Drawer.Body>
                <Drawer.CloseTrigger>
                  <CloseButton size='sm' />
                </Drawer.CloseTrigger>
              </Drawer.Content>
            </Drawer.Positioner>
          </Portal>
        </Drawer.Root>
      )}
      <Box
        w={isFullScreen ? '100%' : ['100%', '100%', '100%', '90%']}
        margin={'auto'}
        paddingLeft={isFullScreen ? '0px' : '5px'}
        paddingRight={isFullScreen ? '0px' : '5px'}
        paddingTop={isFullScreen ? '0px' : '10px'}
        {...props.containerProps}>
        <Outlet />
      </Box>
    </Box>
  );
};
